import { View, Text } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import type { BreedRanking } from "../app/lib/types";

interface Props {
  rankings: BreedRanking[];
  breed: string;
  /** User's dog's best 3 avg speed for the personal comparison line. Skip if 0/null. */
  userBestThreeAvgSpeed?: number | null;
  /** When set, the comparison line uses "{dogDisplayName}'s best 3 avg" instead of "Your dog's…".
   *  Used on public profile pages where the viewer isn't the dog's owner. */
  dogDisplayName?: string | null;
}

// Fast CAT physics: 100 yards covered in t seconds → mph = 204.545 / t
const mphToSeconds = (mph: number) => 204.545 / mph;

export function DashFact({ rankings, breed, userBestThreeAvgSpeed, dogDisplayName }: Props) {
  const topSpeeds = rankings
    .filter((r) => r.speed != null && (r.speed as number) > 0)
    .slice(0, 3)
    .map((r) => r.speed as number);

  if (topSpeeds.length === 0) return null;

  const avgTopSpeed = topSpeeds.reduce((a, b) => a + b, 0) / topSpeeds.length;
  const avgTopTime = mphToSeconds(avgTopSpeed);

  const userTime = userBestThreeAvgSpeed && userBestThreeAvgSpeed > 0
    ? mphToSeconds(userBestThreeAvgSpeed)
    : null;
  const timeGap = userTime != null ? userTime - avgTopTime : null;

  return (
    <View
      style={{
        backgroundColor: "#FFF7E6",
        borderLeftWidth: 3,
        borderLeftColor: "#F5A623",
        borderRadius: 8,
        padding: 14,
        marginBottom: 16,
        width: "100%",
        alignSelf: "stretch",
      }}
    >
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <Ionicons name="paw" size={16} color="#F5A623" />
        <Text style={{ fontSize: 12, color: "#92400e", fontWeight: "800", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Dash-Fact
        </Text>
      </View>
      <Text style={{ fontSize: 14, color: "#1B2A4A", lineHeight: 20 }}>
        Top 3 <Text style={{ fontWeight: "700" }}>{breed}{topSpeeds.length === 1 ? "" : "s"}</Text> run an average of
      </Text>
      <Text style={{ fontSize: 16, fontWeight: "800", color: "#F5A623", marginTop: 2, lineHeight: 22 }}>
        {avgTopTime.toFixed(2)}s
        <Text style={{ fontSize: 14, fontWeight: "600", color: "#1B2A4A" }}>{" "}({avgTopSpeed.toFixed(2)} mph)</Text>
      </Text>
      {userTime != null && timeGap != null && (
        <View style={{ marginTop: 6 }}>
          <Text style={{ fontSize: 13, color: "#1B2A4A", lineHeight: 19 }}>
            {dogDisplayName ? `${dogDisplayName}'s` : "Your dog's"} best 3 avg:{" "}
            <Text style={{ fontWeight: "800" }}>{userTime.toFixed(2)}s</Text>
            {" ("}{userBestThreeAvgSpeed!.toFixed(2)} mph{")"}
          </Text>
          {timeGap > 0.05 ? (
            <Text style={{ fontSize: 13, fontWeight: "800", color: "#92400e", marginTop: 2, lineHeight: 19 }}>
              {timeGap.toFixed(2)}s behind the top.
            </Text>
          ) : timeGap < -0.05 ? (
            <Text style={{ fontSize: 13, fontWeight: "800", color: "#15803d", marginTop: 2, lineHeight: 19 }}>
              {Math.abs(timeGap).toFixed(2)}s ahead of the top!
            </Text>
          ) : (
            <Text style={{ fontSize: 13, fontWeight: "800", color: "#1B2A4A", marginTop: 2, lineHeight: 19 }}>
              Right with the leaders.
            </Text>
          )}
        </View>
      )}
    </View>
  );
}
